import { Dictionary } from '../models/SharedTypes';

export enum OperatingSystems {
    WinPhone = 'Windows Phone',
    WinRT = 'Windows RT',
    Win = 'Windows',
    iOS = 'iOS',
    MacOS = 'Mac OS X',
    Android = 'Android',
    ChromeOS = 'ChromeOS',
    Linux = 'Linux',
    Unknown = 'Unknown',
}

const osRegExps: Dictionary<RegExp> = {};
osRegExps[OperatingSystems.WinPhone] = /windows\sphone/i;
osRegExps[OperatingSystems.WinRT] = /arm;/i;
osRegExps[OperatingSystems.Win] = /(windows|win32)/i;
osRegExps[OperatingSystems.iOS] = /(iPad|iPhone|iPod)/i;
osRegExps[OperatingSystems.MacOS] = /(macintosh|mac os x)/i;
osRegExps[OperatingSystems.Android] = /android/i;
osRegExps[OperatingSystems.ChromeOS] = /CrOS/i;
osRegExps[OperatingSystems.Linux] = /(linux|x11)/i;

export enum Browsers {
    Opera = 'Opera',
    EdgeOld = 'EdgeOld',
    Edge = 'Edge',
    MSIE = 'IE',
    Chrome = 'Chrome',
    Firefox = 'Firefox',
    Safari = 'Safari',
    Facebook = 'Facebook', // inApp browser detection
    Unknown = 'Unknown',
}

const browserRegExps: Dictionary<RegExp> = {};
browserRegExps[Browsers.Opera] = /(OPR\/|Opera)/i;
browserRegExps[Browsers.EdgeOld] = /Edge/i;
browserRegExps[Browsers.Edge] = /Edg/i;
browserRegExps[Browsers.MSIE] = /Trident/i;
browserRegExps[Browsers.Chrome] = /Chrome/i;
browserRegExps[Browsers.Firefox] = /Firefox/i;
browserRegExps[Browsers.Safari] = /Safari/i;
browserRegExps[Browsers.Facebook] = /(FBAN|FBAV)/i;

export interface IBrowserVersion {
    major: number;
    minor: number;
    bugfix?: number;
}

export enum DeviceTypes {
    Desktop = 'desktop',
    Mobile = 'mobile',
    Other = 'other',
}

export class EnvironmentHelper {

    /**
     * Returns the Operating System the code is executed in
     *
     * @return {string}
     */
    static getOS = (_userAgent: string = userAgent): OperatingSystems => {
        if (_userAgent.match(osRegExps[OperatingSystems.WinPhone])) {
            return OperatingSystems.WinPhone;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.WinRT])) {
            return OperatingSystems.WinRT;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.Win])) {
            return OperatingSystems.Win;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.iOS])) {
            return OperatingSystems.iOS;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.MacOS])) {
            // iPads send by default MacOS user agent to so correctly detect iPad we use this trick
            if (navigator?.maxTouchPoints > 1) {
                return OperatingSystems.iOS;
            }

            return OperatingSystems.MacOS;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.Android])) {
            return OperatingSystems.Android;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.ChromeOS])) {
            return OperatingSystems.ChromeOS;
        }

        if (_userAgent.match(osRegExps[OperatingSystems.Linux])) {
            return OperatingSystems.Linux;
        }

        return OperatingSystems.Unknown;
    }

    /**
     * Returns the browser name
     *
     * @return {string}
     */
    static getBrowserName = (_userAgent: string = userAgent): Browsers => {

        if (_userAgent.match(browserRegExps[Browsers.Opera])) {
            return Browsers.Opera;
        }

        if (_userAgent.match(browserRegExps[Browsers.Edge])) {
            return Browsers.Edge;
        }

        if (_userAgent.match(browserRegExps[Browsers.EdgeOld])) {
            return Browsers.EdgeOld;
        }

        if (_userAgent.match(browserRegExps[Browsers.MSIE])) {
            return Browsers.MSIE;
        }

        if (_userAgent.match(browserRegExps[Browsers.Chrome])) {
            return Browsers.Chrome;
        }

        if (_userAgent.match(browserRegExps[Browsers.Firefox])) {
            return Browsers.Firefox;
        }

        if (_userAgent.match(browserRegExps[Browsers.Safari])) {
            return Browsers.Safari;
        }

        if (_userAgent.match(browserRegExps[Browsers.Facebook])) {
            return Browsers.Facebook;
        }

        return Browsers.Unknown;
    }

    static getSafariBrowserVersion = (_userAgent: string = userAgent): IBrowserVersion | undefined => {
        const version = _userAgent.match(/version\/(\d+)\.(\d+)(\.(\d+))?/i);

        if (!version) {
            return;
        }

        return {
            major: parseInt(version[1], 10),
            minor: parseInt(version[2], 10),
            bugfix: parseInt(version[4], 10) || undefined,
        };
    }

    static isSafariBrowserVersionAtLeast(minimumVersion: IBrowserVersion, _userAgent: string = userAgent): boolean {
        if (EnvironmentHelper.getBrowserName(_userAgent) !== Browsers.Safari) {
            return false;
        }

        const version = EnvironmentHelper.getSafariBrowserVersion(_userAgent);

        if (!version) {
            return false;
        }

        return version.major > minimumVersion.major
            || (version.major === minimumVersion.major && version.minor >= minimumVersion.minor);
    }

    static isSafariBrowserEligibleForMeetingGuestFlow = (_userAgent: string = userAgent): boolean => {
        return EnvironmentHelper.isSafariBrowserVersionAtLeast({
            major: 13,
            minor: 1,
        }, _userAgent);
    }

    /**
     * Returns the device type
     *
     * @return {string}
     */
    static getDeviceType = (_os: OperatingSystems = os): DeviceTypes => {
        switch (_os) {
            case OperatingSystems.Win:
            case OperatingSystems.MacOS:
            case OperatingSystems.WinRT:
            case OperatingSystems.ChromeOS:
            case OperatingSystems.Linux:
                return DeviceTypes.Desktop;

            case OperatingSystems.WinPhone:
            case OperatingSystems.iOS:
            case OperatingSystems.Android:
                return DeviceTypes.Mobile;

            case OperatingSystems.Unknown:
            default:
                return DeviceTypes.Other;
        }
    }

    static canJoinOnWeb = (_deviceType: DeviceTypes = deviceType, _os: OperatingSystems = os, _browser: Browsers = browser): boolean => {
        if (_deviceType !== DeviceTypes.Desktop) {
            return false;
        }

        let supportedBrowser: Browsers[] = [];
        switch (_os) {
            case OperatingSystems.MacOS:
                // atm we want to enable Safari users to join on web
                // only in UnifiedLogin flow
                // when allowDesktopSafariGuestFlow is true
                // and the user is not authenticated
                // (though currently it seems we are not detecting properly authenticated users in Safari)
                supportedBrowser = [Browsers.Chrome, Browsers.Edge];
                break;

            case OperatingSystems.Win:
            case OperatingSystems.WinRT:
                supportedBrowser = [Browsers.Chrome, Browsers.Edge, Browsers.EdgeOld];
                break;

            case OperatingSystems.Linux:
            default:
                supportedBrowser = [Browsers.Chrome, Browsers.Edge];
                break;
        }

        return supportedBrowser.indexOf(_browser) > -1;
    }

    static canJoinOnWebIncludingSafari = (isGuest: boolean, _userAgent: string = userAgent): boolean => {
        const _os = EnvironmentHelper.getOS(_userAgent);

        return EnvironmentHelper.canJoinOnWeb(EnvironmentHelper.getDeviceType(_os), _os, EnvironmentHelper.getBrowserName(_userAgent))
            || (isGuest && _os === OperatingSystems.MacOS && EnvironmentHelper.isSafariBrowserEligibleForMeetingGuestFlow(_userAgent));
    }

    /**
     * Returns current screen resolution
     *
     * @return {string}
     */
    static getScreenResolution = (_screen = window.screen) => {
        if (_screen.width == null || typeof _screen.width === 'undefined' ||
            _screen.height == null || typeof _screen.height === 'undefined') {

            return 'Unknown';
        }

        return _screen.width + 'x' + _screen.height;
    }

    /**
     * Returns language from the browser
     *
     * @return {string}
     */
    static getLanguage = (_navigator = window.navigator) => {
        const lang = _navigator.language || (_navigator as any).userLanguage;

        if (lang !== undefined && lang.length > 2) {
            return lang.substr(0, 2);
        }

        return lang;
    }

    /**
     * Returns the iOS version
     * Based on detectDevice can returns iPhone/iPad or just version number
     *
     * @return {string}
     */
    static getIOSVersion(_userAgent = userAgent) {

        const iOSVersionMatches = userAgent.match(
            new RegExp('(iPhone OS|CPU OS)' + ' ([\\d_]+)'),
        );

        if (iOSVersionMatches) {
            return iOSVersionMatches[2].replace(/_/g, '.');
        }

        return 'Unknown';
    }

    static isIOSInAppBrowserFlow = (_os: OperatingSystems = os, _browser: Browsers = browser): boolean => {
        const knownInAppBrowsers = [
            Browsers.Facebook,
        ];

        return _os === OperatingSystems.iOS && knownInAppBrowsers.indexOf(browser) > -1;
    }
}

// PlatformVersion: Helpers.Environment.pureOSVersion,
//             Platform: Helpers.Environment.platform,
//             Language: Helpers.Environment.language,
//             ScreenResolution: Helpers.Environment.screenResolution

const userAgent = window.navigator.userAgent;
export const os = EnvironmentHelper.getOS();
export const browser = EnvironmentHelper.getBrowserName();
export const deviceType = EnvironmentHelper.getDeviceType();
export const canJoinOnWeb = EnvironmentHelper.canJoinOnWeb();
export const screenResolution = EnvironmentHelper.getScreenResolution();
export const deviceLanguage = EnvironmentHelper.getLanguage();
export const platform = window.navigator.platform;
export const isIOSInAppBrowserFlow = EnvironmentHelper.isIOSInAppBrowserFlow();
